import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Frame, RotateCcw, Image as ImageIcon } from 'lucide-react';

type BorderStyle = 'solid' | 'gradient' | 'pattern' | 'shadow';
type PatternType = 'stripe' | 'dots';
type OutputFormat = 'png' | 'jpg';

const BORDER_STYLES: { value: BorderStyle; label: string; description: string }[] = [
  { value: 'solid', label: 'Solid Color', description: 'Single color border' },
  { value: 'gradient', label: 'Gradient', description: 'Two-color gradient border' },
  { value: 'pattern', label: 'Pattern', description: 'Stripe or dot patterns' },
  { value: 'shadow', label: 'Shadow Only', description: 'Drop shadow without border' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AddBorder: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [borderWidth, setBorderWidth] = useState(20);
  const [borderStyle, setBorderStyle] = useState<BorderStyle>('solid');
  const [color1, setColor1] = useState('#000000');
  const [color2, setColor2] = useState('#ffffff');
  const [patternType, setPatternType] = useState<PatternType>('stripe');
  const [cornerRadius, setCornerRadius] = useState(0);
  const [padding, setPadding] = useState(0);
  const [shadowEnabled, setShadowEnabled] = useState(false);
  const [shadowBlur, setShadowBlur] = useState(15);
  const [shadowOffsetX, setShadowOffsetX] = useState(5);
  const [shadowOffsetY, setShadowOffsetY] = useState(5);
  const [shadowColor, setShadowColor] = useState('#000000');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const drawCheckerboard = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const size = 10;
    for (let y = 0; y < h; y += size) {
      for (let x = 0; x < w; x += size) {
        ctx.fillStyle = (Math.floor(x / size) + Math.floor(y / size)) % 2 === 0 ? '#e5e5e5' : '#ffffff';
        ctx.fillRect(x, y, size, size);
      }
    }
  }, []);

  const applyBorder = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement, forPreview: boolean) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const bw = borderStyle === 'shadow' ? 0 : borderWidth;
    const totalPad = bw + padding;
    const shadowExtra = (shadowEnabled || borderStyle === 'shadow') ? shadowBlur + Math.max(Math.abs(shadowOffsetX), Math.abs(shadowOffsetY)) + 10 : 0;
    const cw = iw + totalPad * 2 + shadowExtra * 2;
    const ch = ih + totalPad * 2 + shadowExtra * 2;

    canvas.width = cw;
    canvas.height = ch;

    // Checkerboard background for preview
    if (forPreview) {
      drawCheckerboard(ctx, cw, ch);
    } else {
      ctx.clearRect(0, 0, cw, ch);
    }

    const ox = shadowExtra + totalPad;
    const oy = shadowExtra + totalPad;

    // Draw border area
    if (borderStyle !== 'shadow' && bw > 0) {
      const bx = shadowExtra;
      const by = shadowExtra;
      const bWidth = iw + totalPad * 2;
      const bHeight = ih + totalPad * 2;

      ctx.save();
      if (cornerRadius > 0) {
        const r = cornerRadius;
        ctx.beginPath();
        ctx.moveTo(bx + r, by);
        ctx.lineTo(bx + bWidth - r, by);
        ctx.quadraticCurveTo(bx + bWidth, by, bx + bWidth, by + r);
        ctx.lineTo(bx + bWidth, by + bHeight - r);
        ctx.quadraticCurveTo(bx + bWidth, by + bHeight, bx + bWidth - r, by + bHeight);
        ctx.lineTo(bx + r, by + bHeight);
        ctx.quadraticCurveTo(bx, by + bHeight, bx, by + bHeight - r);
        ctx.lineTo(bx, by + r);
        ctx.quadraticCurveTo(bx, by, bx + r, by);
        ctx.closePath();
        ctx.clip();
      }

      if (borderStyle === 'solid') {
        ctx.fillStyle = color1;
        ctx.fillRect(bx, by, bWidth, bHeight);
      } else if (borderStyle === 'gradient') {
        const grad = ctx.createLinearGradient(bx, by, bx + bWidth, by + bHeight);
        grad.addColorStop(0, color1);
        grad.addColorStop(1, color2);
        ctx.fillStyle = grad;
        ctx.fillRect(bx, by, bWidth, bHeight);
      } else if (borderStyle === 'pattern') {
        ctx.fillStyle = color1;
        ctx.fillRect(bx, by, bWidth, bHeight);
        ctx.fillStyle = color2;
        if (patternType === 'stripe') {
          const stripeW = 6;
          for (let i = -bWidth; i < bWidth + bHeight; i += stripeW * 2) {
            ctx.beginPath();
            ctx.moveTo(bx + i, by);
            ctx.lineTo(bx + i + bHeight, by + bHeight);
            ctx.lineTo(bx + i + bHeight + stripeW, by + bHeight);
            ctx.lineTo(bx + i + stripeW, by);
            ctx.closePath();
            ctx.fill();
          }
        } else if (patternType === 'dots') {
          const dotSpacing = 12;
          const dotR = 2;
          for (let y = by; y < by + bHeight; y += dotSpacing) {
            for (let x = bx; x < bx + bWidth; x += dotSpacing) {
              ctx.beginPath();
              ctx.arc(x + dotSpacing / 2, y + dotSpacing / 2, dotR, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
      }
      ctx.restore();
    }

    // Clear inner area for image (including padding)
    if (borderStyle !== 'shadow' && bw > 0) {
      const innerX = shadowExtra + bw;
      const innerY = shadowExtra + bw;
      const innerW = iw + padding * 2;
      const innerH = ih + padding * 2;

      ctx.save();
      if (cornerRadius > 0) {
        const r = Math.max(0, cornerRadius - bw);
        ctx.beginPath();
        ctx.moveTo(innerX + r, innerY);
        ctx.lineTo(innerX + innerW - r, innerY);
        ctx.quadraticCurveTo(innerX + innerW, innerY, innerX + innerW, innerY + r);
        ctx.lineTo(innerX + innerW, innerY + innerH - r);
        ctx.quadraticCurveTo(innerX + innerW, innerY + innerH, innerX + innerW - r, innerY + innerH);
        ctx.lineTo(innerX + r, innerY + innerH);
        ctx.quadraticCurveTo(innerX, innerY + innerH, innerX, innerY + innerH - r);
        ctx.lineTo(innerX, innerY + r);
        ctx.quadraticCurveTo(innerX, innerY, innerX + r, innerY);
        ctx.closePath();
        ctx.clip();
      }
      // Fill padding area with white/transparent
      if (padding > 0) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(innerX, innerY, innerW, innerH);
      }
      ctx.restore();
    }

    // Draw image with optional shadow
    ctx.save();
    if (cornerRadius > 0) {
      const r = Math.max(0, cornerRadius - bw);
      ctx.beginPath();
      ctx.moveTo(ox + r, oy);
      ctx.lineTo(ox + iw - r, oy);
      ctx.quadraticCurveTo(ox + iw, oy, ox + iw, oy + r);
      ctx.lineTo(ox + iw, oy + ih - r);
      ctx.quadraticCurveTo(ox + iw, oy + ih, ox + iw - r, oy + ih);
      ctx.lineTo(ox + r, oy + ih);
      ctx.quadraticCurveTo(ox, oy + ih, ox, oy + ih - r);
      ctx.lineTo(ox, oy + r);
      ctx.quadraticCurveTo(ox, oy, ox + r, oy);
      ctx.closePath();
      ctx.clip();
    }

    if (shadowEnabled || borderStyle === 'shadow') {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;
    }

    ctx.drawImage(img, ox, oy, iw, ih);
    ctx.restore();
  }, [borderWidth, borderStyle, color1, color2, patternType, cornerRadius, padding, shadowEnabled, shadowBlur, shadowOffsetX, shadowOffsetY, shadowColor, drawCheckerboard]);

  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    applyBorder(previewCanvasRef.current, imgElement, true);
  }, [imgElement, applyBorder]);

  const handleDownload = useCallback(() => {
    if (!imgElement) return;
    const canvas = document.createElement('canvas');
    applyBorder(canvas, imgElement, false);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;
    const dataUrl = canvas.toDataURL(mimeType, quality);
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'bordered';
    link.download = `${baseName}-bordered.${outputFormat === 'jpg' ? 'jpg' : 'png'}`;
    link.href = dataUrl;
    link.click();
  }, [imgElement, applyBorder, outputFormat, jpgQuality, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setImageDimensions(null);
    setBorderWidth(20);
    setBorderStyle('solid');
    setColor1('#000000');
    setColor2('#ffffff');
    setCornerRadius(0);
    setPadding(0);
    setShadowEnabled(false);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Frame className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Add Border</h1>
        </div>
        <p className="text-gray-600">Add customizable borders, rounded corners, and shadows to your images.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Border Settings</h2>
              <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>

            {/* Border style */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Border Style</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {BORDER_STYLES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setBorderStyle(s.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      borderStyle === s.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Border width (not for shadow-only) */}
            {borderStyle !== 'shadow' && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Border Width</label>
                  <span className="text-sm font-mono text-emerald-600">{borderWidth}px</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={borderWidth}
                  onChange={(e) => setBorderWidth(Number(e.target.value))}
                  className="w-full accent-emerald-600"
                />
              </div>
            )}

            {/* Colors */}
            <div className="flex flex-wrap gap-4">
              {(borderStyle === 'solid' || borderStyle === 'pattern') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {borderStyle === 'pattern' ? 'Background Color' : 'Border Color'}
                  </label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={color1} onChange={(e) => setColor1(e.target.value)} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                    <span className="text-sm font-mono text-gray-500">{color1}</span>
                  </div>
                </div>
              )}
              {(borderStyle === 'gradient' || borderStyle === 'pattern') && (
                <>
                  {borderStyle === 'gradient' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color 1</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={color1} onChange={(e) => setColor1(e.target.value)} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                        <span className="text-sm font-mono text-gray-500">{color1}</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {borderStyle === 'pattern' ? 'Pattern Color' : 'Color 2'}
                    </label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={color2} onChange={(e) => setColor2(e.target.value)} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                      <span className="text-sm font-mono text-gray-500">{color2}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Pattern type */}
            {borderStyle === 'pattern' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pattern Type</label>
                <div className="flex gap-2">
                  {(['stripe', 'dots'] as PatternType[]).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPatternType(p)}
                      className={`px-4 py-2 rounded-lg border text-sm capitalize transition-colors ${
                        patternType === p
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Corner radius */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Corner Radius</label>
                <span className="text-sm font-mono text-emerald-600">{cornerRadius}px</span>
              </div>
              <input
                type="range"
                min={0}
                max={50}
                value={cornerRadius}
                onChange={(e) => setCornerRadius(Number(e.target.value))}
                className="w-full accent-emerald-600"
              />
            </div>

            {/* Padding */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Padding</label>
                <span className="text-sm font-mono text-emerald-600">{padding}px</span>
              </div>
              <input
                type="range"
                min={0}
                max={50}
                value={padding}
                onChange={(e) => setPadding(Number(e.target.value))}
                className="w-full accent-emerald-600"
              />
            </div>

            {/* Shadow */}
            <div className="border border-gray-100 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="shadowToggle"
                  checked={shadowEnabled || borderStyle === 'shadow'}
                  onChange={(e) => setShadowEnabled(e.target.checked)}
                  disabled={borderStyle === 'shadow'}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="shadowToggle" className="text-sm font-medium text-gray-700">
                  Drop Shadow {borderStyle === 'shadow' ? '(always on for shadow style)' : ''}
                </label>
              </div>
              {(shadowEnabled || borderStyle === 'shadow') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Blur: {shadowBlur}px</label>
                    <input type="range" min={0} max={50} value={shadowBlur} onChange={(e) => setShadowBlur(Number(e.target.value))} className="w-full accent-emerald-600" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Offset X: {shadowOffsetX}px</label>
                    <input type="range" min={-30} max={30} value={shadowOffsetX} onChange={(e) => setShadowOffsetX(Number(e.target.value))} className="w-full accent-emerald-600" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Offset Y: {shadowOffsetY}px</label>
                    <input type="range" min={-30} max={30} value={shadowOffsetY} onChange={(e) => setShadowOffsetY(Number(e.target.value))} className="w-full accent-emerald-600" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Shadow Color</label>
                    <input type="color" value={shadowColor} onChange={(e) => setShadowColor(e.target.value)} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                  </div>
                </div>
              )}
            </div>

            {/* Output format */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="png">PNG</option>
                  <option value="jpg">JPG</option>
                </select>
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quality: {jpgQuality}%</label>
                  <input type="range" min={10} max={100} value={jpgQuality} onChange={(e) => setJpgQuality(Number(e.target.value))} className="w-full accent-emerald-600" />
                </div>
              )}
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
              <span>{file.name}</span>
            </div>
          )}

          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Preview (checkerboard = transparency)</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100 p-4 flex justify-center">
              <canvas ref={previewCanvasRef} className="max-w-full h-auto" style={{ maxHeight: '600px' }} />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              <Download className="w-5 h-5" /> Download Bordered Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddBorder;
